// YERİ: src/app/domains/site/core/tenant-web.service.ts
// Tenant (müşteri) public web sitesi — AllowAnonymous /api/client-web/{clientId}
// endpoint'lerini tüketir. Hem portal önizleme hem ileride public domain render
// için ortak servis. Section render katmanının veri kaynağı.

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { API_BASE } from '@/app/core/auth/auth.service';

/** Vitrinde gösterilen blog yazısı (yayımlanmış). */
export interface TenantBlogPost {
  id: number;
  title: string;
  slug: string;
  summary?: string | null;
  coverImageUrl?: string | null;
  publishedAt?: string | null;
}

// ── Spor kulübü public tipleri ────────────────────────────────────────────

export interface TenantFixture {
  id: number;
  competitionName?: string | null;
  /** Hafta/tur numarası; 0 ise gösterilmez. */
  round: number;
  homeTeamName: string;
  awayTeamName: string;
  scheduledAt?: string | null;
  venue?: string | null;
  scoreHome?: number | null;
  scoreAway?: number | null;
  status: string;
}

export interface TenantStandingRow {
  rank: number;
  teamName: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
}

export interface TenantStandings {
  competitionId: number;
  competitionName: string;
  season?: string | null;
  rows: TenantStandingRow[];
}

export interface TenantFormationSpot {
  /** Sahanın SOL kenarından yüzde (0-100). */
  x: number;
  /** Sahanın ÜST kenarından yüzde (0-100). */
  y: number;
  label?: string | null;
  playerName?: string | null;
  number?: string | null;
}

// ── Sektörel public tipler (portfolyo, özgeçmiş, paket, vaaz, etkinlik…) ──

export interface TenantPortfolioItem {
  id: number;
  title: string;
  slug?: string | null;
  summary?: string | null;
  coverImageUrl?: string | null;
  clientName?: string | null;
  projectUrl?: string | null;
  /** Virgülle ayrılmış etiketler, sunucudan geldiği gibi. */
  tags?: string | null;
  completedAt?: string | null;
  isFeatured: boolean;
}

export interface TenantResumeEntry {
  id: number;
  /** experience | education | skill | certificate … */
  entryType: string;
  title: string;
  organization?: string | null;
  location?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  isCurrent: boolean;
  description?: string | null;
  /** Yalnız beceri satırlarında dolu (0-100). */
  skillLevel?: number | null;
}

export interface TenantPackage {
  id: number;
  name: string;
  description?: string | null;
  type?: string | null;
  price?: number | null;
  currency?: string | null;
  sessionCount?: number | null;
  durationDays?: number | null;
  billingPeriod?: string | null;
}

export interface TenantSermon {
  id: number;
  title: string;
  speaker?: string | null;
  deliveredAt?: string | null;
  category?: string | null;
  topic?: string | null;
  summary?: string | null;
  mediaUrl?: string | null;
}

export interface TenantPublicEvent {
  id: number;
  title: string;
  description?: string | null;
  category?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  location?: string | null;
  speaker?: string | null;
  capacity?: number | null;
}

export interface TenantCampaign {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
  goalAmount?: number | null;
  /** Toplam bağış; bağışçı kimliği HİÇ dönmez. */
  raisedAmount: number;
  currency?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  status: string;
}

export interface TenantVenue {
  id: number;
  name: string;
  city?: string | null;
  address?: string | null;
  capacity?: number | null;
  dailyRate?: number | null;
  currency?: string | null;
}

export interface TenantLoyaltyTier {
  id: number;
  name: string;
  minPoints: number;
  discountPercent: number;
  badgeColor?: string | null;
}

// ── Katalog public tipleri (menü, oda, ilan, ders, araç, tarife…) ────────

export interface TenantMenuItem {
  id: number; name: string; description?: string | null;
  price: number; currency?: string | null;
  /** Alerjen uyarısı — ziyaretçinin bilmesi GEREKEN bilgi. */
  allergens?: string | null;
  photoUrl?: string | null; prepMinutes?: number | null;
}
export interface TenantMenuCategory { id: number; name: string; items: TenantMenuItem[]; }

export interface TenantRoomType {
  id: number; name: string; basePrice: number; maxOccupancy: number;
  bedConfig?: string | null; sizeM2?: number | null; amenitiesText?: string | null;
}
export interface TenantAmenity { id: number; name: string; kind?: string | null; description?: string | null; }

export interface TenantProperty {
  id: number; title: string; propertyType?: string | null;
  /** sale | rent */
  purpose?: string | null;
  price: number; currency?: string | null; areaSqm: number;
  roomLayout?: string | null; city?: string | null; district?: string | null;
  description?: string | null;
  /** active | under_offer */
  status: string;
}

export interface TenantCourse {
  id: number; code?: string | null; name: string; description?: string | null;
  credits: number; capacity: number; scheduleText?: string | null;
}

export interface TenantVehicle {
  id: number; make: string; model: string; year?: number | null;
  color?: string | null; mileage?: number | null;
  /** sale | rental — servisteki MÜŞTERİ aracı hiç dönmez. */
  purpose: string;
}

export interface TenantShippingRate {
  id: number; name: string; serviceType?: string | null;
  zoneName?: string | null; zoneCoverage?: string | null;
  minWeightKg: number; maxWeightKg: number;
  price: number; currency?: string | null; surcharge: number;
}

export interface TenantConstructionProject {
  id: number; name: string; projectType?: string | null; description?: string | null;
  location?: string | null; startDate?: string | null; endDate?: string | null;
  progressPercent: number; status?: string | null;
}

export interface TenantCrop {
  id: number; cropName: string; season?: string | null; expectedHarvestDate?: string | null;
}

export interface TenantBoardingUnit {
  id: number; name: string; unitType?: string | null; zone?: string | null;
  capacity: number; isAvailable: boolean;
}

export interface TenantStorageUnitType {
  id: number; name: string; category?: string | null;
  width: number; depth: number; height?: number | null;
  monthlyRate: number; currency?: string | null; description?: string | null;
  /** Boş birim SAYISI; birim numarası dönmez. */
  availableCount: number;
}

export interface TenantRateCard {
  id: number; serviceName: string; platform?: string | null; contentType?: string | null;
  price: number; currency?: string | null; description?: string | null;
}

export interface TenantChannel {
  id: number; platform?: string | null; handle: string; displayName?: string | null;
  profileUrl?: string | null; niche?: string | null;
  followers: number; engagementRate: number; isVerified: boolean;
}

export interface TenantHomeService {
  id: number; name: string; type?: string | null; description?: string | null;
  unit?: string | null; basePrice: number; currency?: string | null; estimatedMinutes: number;
}

export interface TenantDaycareProgram {
  id: number; name: string; description?: string | null;
  ageMonthsMin?: number | null; ageMonthsMax?: number | null;
  monthlyFee: number; currency?: string | null; capacity: number;
}

export interface TenantFormation {
  id: number;
  name: string;
  teamName?: string | null;
  sport: string;
  spots: TenantFormationSpot[];
}
import { ActiveCompanyService } from '@/app/core/company/active-company.service';

interface ApiResponse<T> { data: T; success: boolean; message: string; }

export interface SiteSection {
  id: number;
  sectionType: string;
  config: string;        // JSON string
  displayOrder: number;
  isVisible: boolean;
}

export interface SitePage {
  id: number;
  slug: string;
  title: string;
  status: string;
  isHomePage: boolean;
  displayOrder: number;
  metaTitle?: string | null;
  metaDescription?: string | null;
  sections: SiteSection[];
}

export interface SitePageSummary {
  id: number; slug: string; title: string; status: string;
  isHomePage: boolean; displayOrder: number;
}

export interface SiteSettings {
  siteTitle?: string | null; logoUrl?: string | null; mobileLogoUrl?: string | null; faviconUrl?: string | null; logoHeight?: string; siteTagline?: string | null; themeJson?: string | null;
  contactEmail?: string | null; contactPhone?: string | null; contactAddress?: string | null;
  googleMapsEmbed?: string | null;
  facebookUrl?: string | null; instagramUrl?: string | null; twitterUrl?: string | null;
  linkedInUrl?: string | null; youtubeUrl?: string | null; whatsAppNumber?: string | null;
  businessHours?: string | null;
  siteMode?: string | null; primarySector?: string | null;
  /**
   * Sitenin fiyat gösterdiği para birimi (ISO 4217) — şirketin birimi.
   * Katalog satırı kendi birimini taşımıyorsa bölümler buna düşer.
   */
  currency?: string | null;
  allowCustomCode?: boolean; customCss?: string | null;
  headerLayout?: string; headerShowMenu?: boolean; headerShowLogo?: boolean; themeKey?: string | null; headerButtonsJson?: string | null;
  /** Bu sitenin sahibi şirketin genel kimliği — "Müşteri Girişi" bağlantısı bunu taşır. */
  memberPortalUid?: string | null;
  /**
   * Sitenin YAYIN DİLİ (ISO 639-1). Ziyaretçiye görünen kabuk metinlerini
   * bu belirler — portalın dili KİRACININ dilidir, ziyaretçinin değil.
   */
  languageCode?: string | null;
}

export interface BookingSvc { id: number; name: string; durationMin?: number | null; price?: number | null; currency?: string | null; moduleSource?: string | null; }
export interface BookingRes { id: number; type: string; name: string; avatarUrl?: string | null; moduleSource?: string | null; }
export interface BookingSlotDto { id: number; resourceId: number; serviceId?: number | null; startTime: string; endTime: string; availableCount: number; status: string; }

/** Vitrin katalog kalemi — fiyat sunucuda hesaplanmis halde gelir. */
export interface ShopProduct {
  id: number;
  categoryId?: number | null;
  categoryName?: string | null;
  name: string;
  description?: string | null;
  type: string;
  unit?: string | null;
  /** Etkin satis fiyati; null ise fiyat tanimsiz (satin alinamaz). */
  price?: number | null;
  currency?: string | null;
  taxRate?: number | null;
  imageUrl?: string | null;
  isPurchasable: boolean;
  inStock: boolean;
  lowStock: boolean;
  displayOrder: number;
}

export interface ShopProductDetail extends ShopProduct {
  images: { url: string; altText?: string | null; isMain: boolean; displayOrder: number }[];
  variants: { id: number; name: string; price?: number | null; inStock: boolean; isPurchasable: boolean }[];
  attributes: { key: string; value: string }[];
}

export interface ShopCategory {
  id: number;
  parentId?: number | null;
  name: string;
  slug?: string | null;
  displayOrder: number;
  productCount: number;
}

/** Sipariş yanıtı — tutarı SUNUCU hesaplar, istemcinin sepet toplamı değil. */
export interface ShopOrderResult {
  success: boolean;
  message: string;
  orderNumber?: string | null;
  grandTotal?: number | null;
  currency?: string | null;
}

/** Vitrinde sunulacak ödeme yöntemleri — hiçbir gizli anahtar içermez. */
export interface PaymentOptions {
  /** bank_transfer | cash_on_delivery | manual */
  offlineMethods: string[];
  bankTransferInfo?: string | null;
  cardEnabled: boolean;
  stripePublishableKey?: string | null;
}

export interface PaymentIntentResult {
  clientSecret: string;
  amount: number;
  currency: string;
}

/** "Ödeme Al" bölümünden başlatılan tahsilat. */
export interface SectionPaymentRequest {
  sectionId: number;
  /** Seçilen kalemin sırası. Serbest tutarda gönderilmez. */
  itemIndex?: number | null;
  /** Yalnız bölüm serbest tutara izin veriyorsa dikkate alınır. */
  amount?: number | null;
  payerName?: string | null;
  payerEmail?: string | null;
  payerPhone?: string | null;
  note?: string | null;
}

export interface SectionPaymentIntentResult extends PaymentIntentResult {
  /**
   * Hangi hesabın publishable key'iyle onaylanacağı. Sunucu kanalı seçtiği
   * için burada dönüyor: kanal kiracının kendi hesabı da olabilir, platform
   * Connect hesabı da. Ayarlardan okunan tek bir anahtara güvenmek, kanal
   * değiştiğinde sessizce yanlış hesapla onaylamaya çalışmak olurdu.
   */
  publishableKey?: string | null;
  paymentId: number;
}

export interface SiteMenuItem {
  id: number; title: string; url: string; parentId?: number | null; displayOrder: number;
  openInNewTab?: boolean; icon?: string | null;
  children?: SiteMenuItem[];
}

@Injectable({ providedIn: 'root' })
export class TenantWebService {
  private http = inject(HttpClient);
  private active = inject(ActiveCompanyService);
  private base(clientId: number) { return `${API_BASE}/client-web/${clientId}`; }

  /** Public endpoint'ler companyId'yi QUERY'den okur (header değil). Giriş yapmış admin
   *  önizlemesinde aktif şirketi ekle; anonim ziyaretçide null → tenant-varsayılan site
   *  (deploy'da domain → şirket çözer). */
  private q(extra = ''): string {
    const cid = this.active.activeCompanyId();
    const parts: string[] = [];
    if (extra) parts.push(extra);
    if (cid != null) parts.push(`companyId=${cid}`);
    return parts.length ? `?${parts.join('&')}` : '';
  }

  getPages(clientId: number): Observable<SitePageSummary[]> {
    return this.http.get<ApiResponse<SitePageSummary[]>>(`${this.base(clientId)}/pages${this.q()}`).pipe(map(r => r.data ?? []));
  }
  getPage(clientId: number, slug: string): Observable<SitePage | null> {
    return this.http.get<ApiResponse<SitePage>>(`${this.base(clientId)}/pages/${slug}${this.q()}`).pipe(map(r => r.data ?? null));
  }
  /** Ana sayfayı çöz: published sayfalardan isHomePage olanı (yoksa ilki) → detay. */
  getHomePage(clientId: number): Observable<SitePage | null> {
    return this.getPages(clientId).pipe(switchMap(pages => {
      if (!pages.length) return of(null);
      const home = pages.find(p => p.isHomePage) ?? pages[0];
      return this.getPage(clientId, home.slug);
    }));
  }
  getSettings(clientId: number): Observable<SiteSettings | null> {
    return this.http.get<ApiResponse<SiteSettings>>(`${this.base(clientId)}/settings${this.q()}`).pipe(map(r => r.data ?? null));
  }
  /** Master (ana şablon) sayfanın görünür section'ları — her sayfada render edilen global bölümler. */
  getMaster(clientId: number): Observable<SiteSection[]> {
    return this.http.get<ApiResponse<{ sections?: SiteSection[] } | null>>(`${this.base(clientId)}/master${this.q()}`)
      .pipe(map(r => r.data?.sections ?? []));
  }
  getMenu(clientId: number, location = 'header'): Observable<SiteMenuItem[]> {
    return this.http.get<ApiResponse<SiteMenuItem[]>>(`${this.base(clientId)}/menu${this.q('location=' + location)}`).pipe(map(r => r.data ?? []));
  }

  /** İletişim formu → lead (CrmEnabled ise CrmContact oluşur). */
  postLead(clientId: number, dto: { name: string; email?: string; phone?: string; message?: string }): Observable<boolean> {
    return this.http.post<ApiResponse<unknown>>(`${this.base(clientId)}/lead${this.q()}`, dto).pipe(map(r => r?.success ?? true));
  }

  /** İletişim formu → "Formlar" gelen kutusu (ClientWebForm). */
  postForm(clientId: number, dto: { name?: string; email?: string; phone?: string; subject?: string; message?: string }): Observable<boolean> {
    const body = { clientId, formType: 'contact', fullName: dto.name, email: dto.email, phone: dto.phone, subject: dto.subject, message: dto.message };
    return this.http.post<ApiResponse<unknown>>(`${API_BASE}/web-builder/forms/submit${this.q()}`, body).pipe(map(r => r?.success ?? true));
  }

  // ── Randevu (booking) — sektörel modülle ortak Booking tablosu ──
  /**
   * Vitrindeki blog bölümünü besleyen yayımlanmış yazılar.
   *
   * Bölüm eskiden yalnız kendi ayarındaki elle yazılmış listeyi basıyordu;
   * liste boşsa ÖRNEK METİN ("Yazı Başlığı 1") gösteriyordu — yani canlı
   * sitede uydurma içerik görünüyordu.
   */
  getBlogPosts(clientId: number, take = 3): Observable<TenantBlogPost[]> {
    return this.http.get<ApiResponse<TenantBlogPost[]>>(
      `${this.base(clientId)}/blog-posts${this.q('take=' + take)}`,
    ).pipe(map(r => r.data ?? []));
  }

  // ── Spor kulübü (sektörel bölümler) ──────────────────────────────────
  // Fikstür/puan durumu/saha planı kulübün KENDİ portal kayıtlarından gelir;
  // bölüm ayarına elle girilmez. Kulüp maçı portalda güncelleyince site de
  // güncellenir.

  /** @param scope upcoming (yaklaşan) | results (sonuçlar) | all */
  getSportsFixtures(clientId: number, scope: 'upcoming' | 'results' | 'all' = 'upcoming', take = 5): Observable<TenantFixture[]> {
    return this.http.get<ApiResponse<TenantFixture[]>>(
      `${this.base(clientId)}/sports/fixtures${this.q(`scope=${scope}&take=${take}`)}`,
    ).pipe(map(r => r.data ?? []));
  }

  /** competitionId verilmezse sunucu en güncel turnuvayı seçer. */
  getSportsStandings(clientId: number, competitionId?: number | null): Observable<TenantStandings | null> {
    return this.http.get<ApiResponse<TenantStandings | null>>(
      `${this.base(clientId)}/sports/standings${this.q(competitionId ? 'competitionId=' + competitionId : '')}`,
    ).pipe(map(r => r.data ?? null));
  }

  /** formationId verilmezse sunucu en son güncellenen planı seçer. */
  getSportsFormation(clientId: number, formationId?: number | null): Observable<TenantFormation | null> {
    return this.http.get<ApiResponse<TenantFormation | null>>(
      `${this.base(clientId)}/sports/formation${this.q(formationId ? 'formationId=' + formationId : '')}`,
    ).pipe(map(r => r.data ?? null));
  }

  // ── Sektörel bölümler ────────────────────────────────────────────────
  // Hepsi kiracının KENDİ portal kayıtlarından besleniyor; bölüm ayarına
  // elle içerik girilmez. Yayın bayrağı (taslak/gizli/yayımlanmamış)
  // sunucuda uygulanır — istemci filtresine güvenilmez.

  getPortfolio(clientId: number, take = 6, featured = false): Observable<TenantPortfolioItem[]> {
    return this.http.get<ApiResponse<TenantPortfolioItem[]>>(
      `${this.base(clientId)}/portfolio${this.q(`take=${take}&featured=${featured}`)}`,
    ).pipe(map(r => r.data ?? []));
  }

  /** entryType boşsa tüm satırlar (deneyim + eğitim + beceri) gelir. */
  getResume(clientId: number, entryType?: string): Observable<TenantResumeEntry[]> {
    return this.http.get<ApiResponse<TenantResumeEntry[]>>(
      `${this.base(clientId)}/resume${this.q(entryType ? 'entryType=' + entryType : '')}`,
    ).pipe(map(r => r.data ?? []));
  }

  getConsultingPackages(clientId: number): Observable<TenantPackage[]> {
    return this.http.get<ApiResponse<TenantPackage[]>>(
      `${this.base(clientId)}/consulting/packages${this.q()}`,
    ).pipe(map(r => r.data ?? []));
  }

  getSermons(clientId: number, take = 6): Observable<TenantSermon[]> {
    return this.http.get<ApiResponse<TenantSermon[]>>(
      `${this.base(clientId)}/religious/sermons${this.q('take=' + take)}`,
    ).pipe(map(r => r.data ?? []));
  }

  /** @param scope upcoming | past | all */
  getReligiousEvents(clientId: number, scope: 'upcoming' | 'past' | 'all' = 'upcoming', take = 5): Observable<TenantPublicEvent[]> {
    return this.http.get<ApiResponse<TenantPublicEvent[]>>(
      `${this.base(clientId)}/religious/events${this.q(`scope=${scope}&take=${take}`)}`,
    ).pipe(map(r => r.data ?? []));
  }

  getCampaigns(clientId: number, activeOnly = true, take = 3): Observable<TenantCampaign[]> {
    return this.http.get<ApiResponse<TenantCampaign[]>>(
      `${this.base(clientId)}/ngo/campaigns${this.q(`activeOnly=${activeOnly}&take=${take}`)}`,
    ).pipe(map(r => r.data ?? []));
  }

  getVenues(clientId: number, take = 6): Observable<TenantVenue[]> {
    return this.http.get<ApiResponse<TenantVenue[]>>(
      `${this.base(clientId)}/events/venues${this.q('take=' + take)}`,
    ).pipe(map(r => r.data ?? []));
  }

  getEventPackages(clientId: number): Observable<TenantPackage[]> {
    return this.http.get<ApiResponse<TenantPackage[]>>(
      `${this.base(clientId)}/events/packages${this.q()}`,
    ).pipe(map(r => r.data ?? []));
  }

  getLoyaltyTiers(clientId: number): Observable<TenantLoyaltyTier[]> {
    return this.http.get<ApiResponse<TenantLoyaltyTier[]>>(
      `${this.base(clientId)}/beauty/loyalty-tiers${this.q()}`,
    ).pipe(map(r => r.data ?? []));
  }

  // ── Katalog bölümleri ────────────────────────────────────────────────
  // Menü, oda, ilan, ders, araç, tarife… hepsi kiracının portal kayıtlarından.
  // Yayın ve gizlilik denetimi SUNUCUDA: müşteri aracı, sözleşme bedeli,
  // tam adres, depo birim numarası ve yemek maliyeti uca HİÇ gelmez.

  getRestaurantMenu(clientId: number): Observable<TenantMenuCategory[]> {
    return this.http.get<ApiResponse<TenantMenuCategory[]>>(
      `${this.base(clientId)}/restaurant/menu${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getHotelRoomTypes(clientId: number): Observable<TenantRoomType[]> {
    return this.http.get<ApiResponse<TenantRoomType[]>>(
      `${this.base(clientId)}/hotel/room-types${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getHotelAmenities(clientId: number): Observable<TenantAmenity[]> {
    return this.http.get<ApiResponse<TenantAmenity[]>>(
      `${this.base(clientId)}/hotel/amenities${this.q()}`).pipe(map(r => r.data ?? []));
  }

  /** @param purpose sale | rent — boşsa hepsi. */
  getProperties(clientId: number, purpose = '', take = 6): Observable<TenantProperty[]> {
    const ek = 'take=' + take + (purpose ? '&purpose=' + purpose : '');
    return this.http.get<ApiResponse<TenantProperty[]>>(
      `${this.base(clientId)}/realty/properties${this.q(ek)}`).pipe(map(r => r.data ?? []));
  }

  getSchoolCourses(clientId: number, take = 12): Observable<TenantCourse[]> {
    return this.http.get<ApiResponse<TenantCourse[]>>(
      `${this.base(clientId)}/school/courses${this.q('take=' + take)}`).pipe(map(r => r.data ?? []));
  }

  /** @param purpose sale | rental — boşsa ikisi de. Müşteri aracı ASLA gelmez. */
  getVehicles(clientId: number, purpose = '', take = 8): Observable<TenantVehicle[]> {
    const ek = 'take=' + take + (purpose ? '&purpose=' + purpose : '');
    return this.http.get<ApiResponse<TenantVehicle[]>>(
      `${this.base(clientId)}/automotive/vehicles${this.q(ek)}`).pipe(map(r => r.data ?? []));
  }

  getShippingRates(clientId: number): Observable<TenantShippingRate[]> {
    return this.http.get<ApiResponse<TenantShippingRate[]>>(
      `${this.base(clientId)}/logistics/rates${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getConstructionProjects(clientId: number, take = 6): Observable<TenantConstructionProject[]> {
    return this.http.get<ApiResponse<TenantConstructionProject[]>>(
      `${this.base(clientId)}/construction/projects${this.q('take=' + take)}`).pipe(map(r => r.data ?? []));
  }

  getCrops(clientId: number, take = 8): Observable<TenantCrop[]> {
    return this.http.get<ApiResponse<TenantCrop[]>>(
      `${this.base(clientId)}/agriculture/crops${this.q('take=' + take)}`).pipe(map(r => r.data ?? []));
  }

  getBoardingUnits(clientId: number): Observable<TenantBoardingUnit[]> {
    return this.http.get<ApiResponse<TenantBoardingUnit[]>>(
      `${this.base(clientId)}/pet/boarding-units${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getStorageUnitTypes(clientId: number): Observable<TenantStorageUnitType[]> {
    return this.http.get<ApiResponse<TenantStorageUnitType[]>>(
      `${this.base(clientId)}/storage/unit-types${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getRateCard(clientId: number): Observable<TenantRateCard[]> {
    return this.http.get<ApiResponse<TenantRateCard[]>>(
      `${this.base(clientId)}/creator/rate-card${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getChannels(clientId: number): Observable<TenantChannel[]> {
    return this.http.get<ApiResponse<TenantChannel[]>>(
      `${this.base(clientId)}/creator/channels${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getHomeServices(clientId: number): Observable<TenantHomeService[]> {
    return this.http.get<ApiResponse<TenantHomeService[]>>(
      `${this.base(clientId)}/homeservices/services${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getDaycarePrograms(clientId: number): Observable<TenantDaycareProgram[]> {
    return this.http.get<ApiResponse<TenantDaycareProgram[]>>(
      `${this.base(clientId)}/daycare/programs${this.q()}`).pipe(map(r => r.data ?? []));
  }

  getBookingServices(clientId: number, moduleSource?: string): Observable<BookingSvc[]> {
    return this.http.get<ApiResponse<BookingSvc[]>>(`${this.base(clientId)}/booking/services${this.q(moduleSource ? 'moduleSource=' + moduleSource : '')}`).pipe(map(r => r.data ?? []));
  }
  getBookingResources(clientId: number, moduleSource?: string): Observable<BookingRes[]> {
    return this.http.get<ApiResponse<BookingRes[]>>(`${this.base(clientId)}/booking/resources${this.q(moduleSource ? 'moduleSource=' + moduleSource : '')}`).pipe(map(r => r.data ?? []));
  }
  getBookingSlots(clientId: number, resourceId: number, date: string): Observable<BookingSlotDto[]> {
    return this.http.get<ApiResponse<BookingSlotDto[]>>(`${this.base(clientId)}/booking/slots${this.q('resourceId=' + resourceId + '&date=' + date)}`).pipe(map(r => r.data ?? []));
  }
  postBooking(clientId: number, dto: { resourceId: number; serviceId?: number | null; guestName: string; guestEmail?: string; guestPhone?: string; startTime: string; endTime: string; notes?: string }): Observable<boolean> {
    return this.http.post<ApiResponse<unknown>>(`${this.base(clientId)}/booking${this.q()}`, dto).pipe(map(r => r?.success ?? true));
  }

  // -- Katalog (e-ticaret) -- ERP urunleri --------------------------------
  // EcommerceEnabled kapaliysa uc bos liste donuyor; burada ayrica kontrol yok.
  getProducts(clientId: number, opts: { categoryId?: number | null; search?: string } = {}): Observable<ShopProduct[]> {
    const extra: string[] = [];
    if (opts.categoryId != null) extra.push('categoryId=' + opts.categoryId);
    if (opts.search) extra.push('search=' + encodeURIComponent(opts.search));
    return this.http
      .get<ApiResponse<ShopProduct[]>>(this.base(clientId) + '/products' + this.q(extra.join('&')))
      .pipe(map(r => r.data ?? []));
  }

  getProduct(clientId: number, productId: number): Observable<ShopProductDetail | null> {
    return this.http
      .get<ApiResponse<ShopProductDetail>>(this.base(clientId) + '/products/' + productId + this.q())
      .pipe(map(r => r.data ?? null));
  }

  getProductCategories(clientId: number): Observable<ShopCategory[]> {
    return this.http
      .get<ApiResponse<ShopCategory[]>>(this.base(clientId) + '/product-categories' + this.q())
      .pipe(map(r => r.data ?? []));
  }


  /**
   * Sipariş oluştur. Kalemlerde YALNIZ productId/variantId/quantity gönderilir;
   * fiyatı sunucu katalogdan okur (istemciden fiyat kabul edilmiyor).
   */
  postOrder(
    clientId: number,
    dto: {
      guestName?: string; guestEmail?: string; guestPhone?: string;
      shippingAddress?: string; notes?: string; paymentMethod?: string;
      items: { productId: number; variantId?: number | null; quantity: number }[];
    },
  ): Observable<ShopOrderResult> {
    return this.http
      .post<ShopOrderResult>(this.base(clientId) + '/order' + this.q(), dto)
      .pipe(map(r => r ?? { success: false, message: '' }));
  }


  getPaymentOptions(clientId: number): Observable<PaymentOptions> {
    return this.http
      .get<ApiResponse<PaymentOptions>>(this.base(clientId) + '/payment-options' + this.q())
      .pipe(map(r => r.data ?? { offlineMethods: [], cardEnabled: false }));
  }


  /**
   * Kart ödemesi için PaymentIntent. Tutar GÖNDERİLMEZ — sunucu siparişi
   * okuyup kendi hesapladığı tutarı kullanır.
   */
  createPaymentIntent(clientId: number, orderNumber: string): Observable<PaymentIntentResult | null> {
    return this.http
      .post<ApiResponse<PaymentIntentResult>>(
        this.base(clientId) + '/payment-intent' + this.q(), { orderNumber })
      .pipe(map(r => r.data ?? null));
  }

  /**
   * "Ödeme Al" bölümü için PaymentIntent. Tutar yalnız bölüm serbest tutara
   * izin veriyorsa gönderilir; sabit kalemlerde sunucu tutarı bölümün
   * yapılandırmasından okur.
   */
  createSectionPaymentIntent(
    clientId: number, req: SectionPaymentRequest,
  ): Observable<SectionPaymentIntentResult | null> {
    return this.http
      .post<ApiResponse<SectionPaymentIntentResult>>(
        this.base(clientId) + '/section-payment-intent' + this.q(), req)
      .pipe(map(r => r.data ?? null));
  }

}
